import React, { useEffect, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockTitle,
  BlockHeadContent,
  Icon,
  BlockBetween,
} from "../../components/Component";
import BasePaginationTable from "../../components/table/BasePaginationTable";
import { defaultParams } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { getListBooking } from "../../slice/bookings";
import { useNavigate } from "react-router-dom";
import get from "lodash/get";
import moment from "moment";
import { debounce } from "lodash";

const getValue = (row, paths, fallback = "N/A") => {
  for (const path of paths) {
    const value = get(row, path);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return fallback;
};

const formatDateTime = (date) =>
  date ? moment(date).format("DD/MM/YYYY HH:mm") : "N/A";

const formatCurrency = (value) =>
  Number.isFinite(value)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      }).format(value)
    : "N/A";

const getNumericValue = (row, paths) => {
  const raw = getValue(row, paths, null);
  if (raw === null) return null;
  const numberValue = Number(raw);
  return Number.isFinite(numberValue) ? numberValue : null;
};

function getDefaultColumns(onViewClick) {
  return [
    {
      head: <span className="sub-text">{"Service"}</span>,
      cell: (row) => (
        <span>{getValue(row, ["service.title", "service.name"])}</span>
      ),
    },
    {
      head: <span className="sub-text">{"Provider"}</span>,
      cell: (row) => (
        <span>{getValue(row, ["provider.fullName", "provider.name"])}</span>
      ),
    },
    {
      head: <span className="sub-text">{"Applier"}</span>,
      cell: (row) => (
        <span>{getValue(row, ["applier.fullName", "applier.name"])}</span>
      ),
    },
    {
      head: <span className="sub-text">{"Status"}</span>,
      cell: (row) => (
        <span className="badge badge-dim bg-outline-primary">
          {getValue(row, ["status", "state", "paymentStatus"])}
        </span>
      ),
    },
    {
      head: <span className="sub-text">{"Payment Status"}</span>,
      cell: (row) => (
        <span>{getValue(row, ["paymentStatus", "payment.status"])}</span>
      ),
    },
    {
      head: <span className="sub-text">{"Price"}</span>,
      cell: (row) => {
        const price = getNumericValue(row, [
          "total",
          "totalPrice",
          "price",
          "amount",
        ]);
        return <span>{formatCurrency(price)}</span>;
      },
    },
    {
      head: <span className="sub-text">{"Admin Price (10%)"}</span>,
      cell: (row) => {
        const price = getNumericValue(row, [
          "total",
          "totalPrice",
          "price",
          "amount",
        ]);
        const adminPrice = Number.isFinite(price) ? price * 0.1 : null;
        return <span>{formatCurrency(adminPrice)}</span>;
      },
    },
    {
      head: <span className="sub-text">{"Provider Price (90%)"}</span>,
      cell: (row) => {
        const price = getNumericValue(row, [
          "total",
          "totalPrice",
          "price",
          "amount",
        ]);
        const providerPrice = Number.isFinite(price) ? price * 0.9 : null;
        return <span>{formatCurrency(providerPrice)}</span>;
      },
    },
    {
      head: <span className="sub-text">{"Created At"}</span>,
      cell: (row) => <span>{formatDateTime(row.createdAt)}</span>,
    },
    {
      head: <span className="sub-text"></span>,
      cell: (row) => (
        <a
          href="#detail"
          className="link link-light"
          onClick={(ev) => {
            ev.preventDefault();
            onViewClick(row);
          }}
        >
          <Icon name="eye"></Icon>
          <span>{"View"}</span>
        </a>
      ),
    },
  ];
}

const BookingsPage = () => {
  const { list, summary, loading } = useSelector((state) => state.bookings);
  const [params, setParams] = useState(defaultParams);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getListBooking(params));
  }, [dispatch, params]);

  const handlePageChange = (page) => {
    setParams({
      ...params,
      page,
    });
  };

  const onView = (booking) => {
    navigate(`/bookings/${booking?._id}`);
  };

  const dataTableColumns = getDefaultColumns(onView);
  const handleSearch = debounce((value) => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      keyword: value,
    }));
  }, 400);

  return (
    <React.Fragment>
      <Head title="Bookings"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle>Bookings</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="form-control-wrap" style={{ minWidth: 260 }}>
                <div className="form-icon form-icon-right">
                  <Icon name="search"></Icon>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search bookings"
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <BasePaginationTable
          list={list}
          loading={loading}
          columns={dataTableColumns}
          onPageChange={handlePageChange}
          onRowClick={onView}
          hasNextPage={summary?.hasNextPage}
          totalItems={summary?.count}
          {...params}
        />
      </Content>
    </React.Fragment>
  );
};

export default BookingsPage;
